import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, Music, Pause } from "lucide-react";
import { Brand } from "@/components/Layout";
import { imgUrl } from "@/components/Gallery";
import { API, api, errMsg } from "@/lib/api";

const FALLBACK = ["/img/mare.jpg", "/img/mare1.jpg", "/img/mare2.jpg", "/img/mare3.jpg", "/img/mare4.jpg"];
const bg = (b, i) => (b.gallery?.length ? `${imgUrl(b.gallery[i % b.gallery.length].id)}?w=1400` : FALLBACK[i % FALLBACK.length]);

function Cover({ b }) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {b.reel ? <video src={imgUrl(b.reel)} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="brochure-reel" /> : <img src={bg(b, 0)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D2B3A]/50 via-[#0D2B3A]/30 to-[#0D2B3A]/85" />
      <div className="relative h-full flex flex-col items-center justify-between text-center text-white p-6 sm:p-10">
        <div className="space-y-3 flex flex-col items-center">
          <div className="text-[9px] sm:text-[10px] tracking-[0.35em] uppercase text-white/80">Afrodite • Tenuta Ledo • Catering • Bovalino</div>
          {b.logos?.length > 0 && <div className="flex gap-3 items-center rounded-full bg-white/85 px-4 py-1.5">{b.logos.map((k) => <img key={k} src={`${API}/public/logo/${k}`} alt="" className="h-6 sm:h-8 w-auto object-contain" />)}</div>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#9FE3E0] mb-4">{[b.evento_tipo, b.dettaglio].filter(Boolean).join(" • ")}</div>
          <h1 className="text-4xl sm:text-6xl leading-tight" style={{ fontFamily: "'Pinyon Script', cursive" }} data-testid="brochure-cover-title">{b.titolo}</h1>
          <div className="mt-4 text-base sm:text-lg text-white/90 italic font-serif-it">{b.sottotitolo}</div>
        </div>
        <div className="text-xs tracking-widest uppercase text-white/80">{[b.data_label, b.sede].filter(Boolean).join(" • ")}</div>
      </div>
    </div>
  );
}

function splitText(text, limit) {
  const t = text || "";
  const parts = t.split(/(?<=[.!?…])\s+/);
  const n = Math.max(1, Math.round(t.length / limit));
  const target = t.length / n;
  const out = [""];
  parts.forEach((s) => {
    const last = out[out.length - 1];
    if (last && out.length < n && last.length + s.length / 2 > target) out.push("");
    out[out.length - 1] += (out[out.length - 1] ? " " : "") + s;
  });
  return out;
}


const SIZES = ["text-[16px] sm:text-[17px] leading-relaxed", "text-[15px] leading-relaxed", "text-[14px] leading-normal", "text-[13px] leading-snug"];

function useFit(deps, max = SIZES.length) {
  const ref = useRef(null);
  const [step, setStep] = useState(0);
  useLayoutEffect(() => {
    setStep(0);
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.scrollHeight > el.clientHeight + 2 && step < max - 1) setStep(step + 1);
  });
  return [ref, step];
}

const MENU_SIZES = [["text-[16px] leading-snug", "space-y-3"], ["text-[15px] leading-snug", "space-y-2.5"], ["text-[14px] leading-snug", "space-y-2"], ["text-[13px] leading-tight", "space-y-2"], ["text-[12px] leading-tight", "space-y-1.5"], ["text-[11px] leading-tight", "space-y-1"], ["text-[12px] leading-tight", "columns-2 gap-4 [&>div]:break-inside-avoid [&>div]:mb-2"], ["text-[10.5px] leading-tight", "columns-2 gap-3 [&>div]:break-inside-avoid [&>div]:mb-1.5"], ["text-[9.5px] leading-tight", "columns-2 gap-3 [&>div]:break-inside-avoid [&>div]:mb-1"]];

function MenuPage({ b, pg, n }) {
  const [ref, step] = useFit([pg, n], MENU_SIZES.length);
  const [size, gap] = MENU_SIZES[step];
  return (
    <div className="absolute inset-0 bg-[#0D2B3A]" data-testid={`brochure-page-${n}`}>
      <Photo b={b} i={pg.img} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D2B3A]/30 via-[#0D2B3A]/10 to-[#0D2B3A]/60" />
      <div ref={ref} className="absolute inset-x-3 sm:inset-x-5 top-[9%] bottom-[5%] rounded-2xl bg-white/[.93] sm:backdrop-blur-md shadow-2xl border border-white/60 px-5 sm:px-7 pt-4 pb-4 overflow-hidden rise" data-testid="brochure-menu-card">
        <Eyebrow b={b} pg={pg} />
        <h2 className="font-serif-it font-semibold text-[#0D2B3A] mt-1 mb-2 leading-tight text-xl sm:text-2xl">{pg.titolo}</h2>
        <div className="w-10 h-px bg-[#C9A96A] mb-2" />
        <div className={gap}>{pg.menu.map((m) => <div key={m.label} className="border-l-2 border-[#C9A96A] pl-3"><div className="uppercase tracking-[0.2em] text-[#0B6E8F] text-[9px] sm:text-[10px]">{m.label}</div><div className={`${size} font-serif-it text-[#1F3B47] whitespace-pre-line`}>{m.value}</div></div>)}</div>
      </div>
      <div className="absolute top-[3%] inset-x-0 text-center font-serif-it italic text-white/90 text-sm tracking-wide drop-shadow">{b.titolo}</div>
      <div className="absolute bottom-1 inset-x-0 text-center text-[9px] text-white/70">{n}</div>
    </div>
  );
}

const Photo = ({ b, i }) => <div className="absolute inset-0 overflow-hidden"><img src={bg(b, i)} alt="" className="kb w-full h-full object-cover" style={{ animationDelay: `-${(i * 3) % 14}s` }} /></div>;

const Body = ({ pg, step, dark }) => (
  <>
    {pg.testo && <p className={`${SIZES[step]} font-serif-it whitespace-pre-line ${dark ? "text-[#1F3B47]" : "text-[#2B4652]"} ${pg.first ? "first-letter:text-[2.6em] first-letter:leading-[0.8] first-letter:float-left first-letter:mr-1.5 first-letter:mt-1 first-letter:text-[#0B6E8F]" : ""}`}>{pg.testo}</p>}
  </>
);

const Eyebrow = ({ b, pg, light }) => <div className={`text-[10px] tracking-[0.3em] uppercase ${light ? "text-white [text-shadow:0_1px_6px_rgba(0,0,0,.6)]" : "text-[#0B6E8F]"}`}>{String(pg.num).padStart(2, "0")} — {b.nomi}</div>;

function TextPage({ b, pg, n }) {
  const [ref, step] = useFit([pg, n]);
  if (pg.menu) return <MenuPage b={b} pg={pg} n={n} />;
  if (pg.first) {
    return (
      <div className="absolute inset-0 bg-[#FBFDFD] flex flex-col" data-testid={`brochure-page-${n}`}>
        <div className="relative h-[36%] shrink-0 overflow-hidden">
          <Photo b={b} i={pg.img} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D2B3A]/85 via-[#0D2B3A]/15 to-transparent" />
          <div className="absolute bottom-0 inset-x-0 px-5 sm:px-8 pb-4 rise"><Eyebrow b={b} pg={pg} light /><h2 className="font-serif-it text-[26px] sm:text-3xl font-semibold text-white mt-1 leading-tight drop-shadow">{pg.titolo}</h2></div>
        </div>
        <div className="h-[3px] shrink-0 bg-gradient-to-r from-[#C9A96A] via-[#F3E3BF] to-transparent" />
        <div ref={ref} className="flex-1 min-h-0 px-5 sm:px-8 pt-4 pb-9 overflow-y-auto brochure-scroll rise" style={{ animationDelay: ".15s" }}><Body pg={pg} step={step} /></div>
        <div className="absolute bottom-2 inset-x-0 text-center text-[9px] text-[#A8C6CF]">{n}</div>
      </div>
    );
  }
  return (
    <div className="absolute inset-0 bg-[#0D2B3A]" data-testid={`brochure-page-${n}`}>
      <Photo b={b} i={pg.img} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D2B3A]/30 via-[#0D2B3A]/10 to-[#0D2B3A]/60" />
      <div className="absolute inset-x-3 sm:inset-x-5 top-[14%] sm:top-[12%] max-h-[80%] rounded-2xl bg-white/[.9] sm:backdrop-blur-md shadow-2xl border border-white/60 flex flex-col rise">
        <div ref={ref} className="min-h-0 px-5 sm:px-7 pt-5 pb-6 overflow-y-auto brochure-scroll">
          <Eyebrow b={b} pg={pg} />
          <h2 className="font-serif-it font-semibold text-[#0D2B3A] mt-1 mb-3 leading-tight text-xl sm:text-2xl">{pg.titolo}{!pg.first && <span className="text-sm font-normal italic text-[#7B929C]"> — {b.lang === "en" ? "continued" : "segue"}</span>}</h2>
          <div className="w-10 h-px bg-[#C9A96A] mb-3" />
          <Body pg={pg} step={step} dark />
        </div>
      </div>
      <div className="absolute top-[5%] inset-x-0 text-center font-serif-it italic text-white/90 text-sm tracking-wide drop-shadow">{b.titolo}</div>
      <div className="absolute bottom-1 inset-x-0 text-center text-[9px] text-white/70">{n}</div>
    </div>
  );
}

function QuotePage({ b, pg, n }) {
  return (
    <div className="absolute inset-0 bg-[#0D2B3A]" data-testid={`brochure-page-${n}`}>
      <Photo b={b} i={pg.img} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D2B3A]/95 via-[#0D2B3A]/55 to-[#0D2B3A]/20" />
      <div className="absolute bottom-0 inset-x-0 p-7 sm:p-10 text-white text-center rise">
        <div className="mx-auto w-12 h-px bg-[#C9A96A] mb-5" />
        <p className="text-2xl sm:text-4xl leading-snug [text-shadow:0_2px_12px_rgba(0,0,0,.55)]" style={{ fontFamily: "'Pinyon Script', cursive" }}>{pg.quote}</p>
        <div className="mt-4 text-[10px] tracking-[0.35em] uppercase text-[#9FE3E0]">{b.nomi}</div>
      </div>
    </div>
  );
}

const firstSentence = (t) => ((t || "").split(/(?<=[.!?…])\s+/)[0] || "").slice(0, 140);

function buildPages(b, mobile) {
  const { clientWidth: vw, clientHeight: vh } = document.documentElement;
  const k = mobile ? Math.min(1.25, Math.max(0.5, ((Math.min(vh - 150, 860)) / 694) * (Math.min(vw * 0.94, 480) / 366))) : 1;
  const out = [];
  let img = 1;
  b.pages.forEach((p, i) => {
    splitText(p.testo, mobile ? 560 * k : 800).forEach((t, j) => out.push({ num: i + 1, titolo: p.titolo, testo: t, first: j === 0, img: img++ }));
    if (i === 3 && b.menu?.length) out.push({ num: i + 1, titolo: b.lang === "en" ? "Your menu" : "Il vostro menù", menu: b.menu, first: true, img: img++ });
    if ((i === 1 || i === 4) && (b.gallery?.length || FALLBACK.length) >= 3 && b.pages[i + 1]) out.push({ quote: firstSentence(b.pages[i + 1].testo) || b.sottotitolo, img: img++ });
  });
  return out;
}

function Back({ b, token }) {
  return (
    <div className="absolute inset-0 bg-[#0D2B3A] text-white flex flex-col items-center justify-center text-center p-8 gap-6">
      {b.logos?.length > 0 && <div className="flex flex-wrap justify-center gap-4 items-center rounded-2xl bg-white/90 px-5 py-3">{b.logos.map((k) => <img key={k} src={`${API}/public/logo/${k}`} alt="" className="h-10 w-auto object-contain" />)}</div>}
      <div className="text-[10px] tracking-[0.35em] uppercase text-[#9FE3E0]">Afrodite Boutique Hotel • Tenuta Ledo • Catering • Bovalino (RC)</div>
      <p className="font-serif-it text-2xl sm:text-3xl leading-snug">{b.chiusura}</p>
      <p className="text-sm text-white/70">Vi aspettiamo per una degustazione o un sopralluogo: rispondete al nostro messaggio WhatsApp.</p>
      <a href={`${API}/public/brochure/${token}/pdf`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-2 rounded-full bg-[#34B3B0] px-5 py-2.5 text-sm hover:bg-[#2A9D8F] transition-colors" data-testid="brochure-back-pdf"><Download size={15} /> {b.lang === "en" ? "Download PDF" : "Scarica in PDF"}</a>
    </div>
  );
}

const Blank = () => <div className="absolute inset-0 bg-[#F3F8F9]" />;

function useMobile() {
  const get = () => {
    const el = document.documentElement;
    const phone = el.clientWidth < 768 || (window.matchMedia("(pointer:coarse)").matches && Math.min(el.clientWidth, el.clientHeight) < 500);
    return phone ? `m${Math.round(el.clientHeight / 60)}` : "";
  };
  const [m, setM] = useState(get);
  useEffect(() => {
    let t;
    const f = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if ((window.visualViewport?.scale || 1) > 1.01) return;
        setM(get());
      }, 300);
    };
    window.addEventListener("resize", f);
    return () => { clearTimeout(t); window.removeEventListener("resize", f); };
  }, []);
  return m;
}

function Leaf({ leaf, i, flipped, z, onClick, mobile, near, active }) {
  return (
    <div onClick={onClick} className={`leaf absolute top-0 h-full cursor-pointer ${active ? "active" : ""}`} data-testid={`brochure-leaf-${i}`}
      style={{ left: mobile ? 0 : "50%", width: mobile ? "100%" : "50%", zIndex: z, transform: `rotateY(${flipped ? -180 : 0}deg)`, opacity: mobile && flipped ? 0 : 1, visibility: near ? "visible" : "hidden" }}>
      <div className="face front">{near && leaf[0]}<div className="curl" /></div>
      <div className="face back">{near && leaf[1]}</div>
    </div>
  );
}

export default function BrochureView() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [cur, setCur] = useState(0);
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  const toggleMusic = () => {
    if (!audio.current) return;
    audio.current.dataset.started = "1";
    if (playing) audio.current.pause();
    else audio.current.play().catch(() => {});
    setPlaying(!playing);
  };
  const mobile = useMobile();
  const touch = useRef(0);
  useEffect(() => {
    api.get(`/public/brochure/${token}`).then((r) => setB(r.data)).catch((e) => setErr(errMsg(e)));
  }, [token]);
  const faces = useMemo(() => (b ? [<Cover b={b} />, ...buildPages(b, mobile).map((pg, i) => (pg.quote ? <QuotePage key={`${mobile}-${i}`} b={b} pg={pg} n={i + 2} /> : <TextPage key={`${mobile}-${i}`} b={b} pg={pg} n={i + 2} />)), <Back b={b} token={token} />] : []), [b, token, mobile]);
  const leaves = useMemo(() => {
    if (mobile) return faces.map((f) => [f, <Blank />]);
    const out = [];
    for (let i = 0; i < faces.length; i += 2) out.push([faces[i], faces[i + 1] || <Blank />]);
    return out;
  }, [faces, mobile]);
  const max = mobile ? leaves.length - 1 : leaves.length;
  const go = (d) => {
    if (audio.current && !playing && !audio.current.dataset.started) {
      audio.current.dataset.started = "1";
      audio.current.play().then(() => setPlaying(true)).catch(() => {});
    }
    setCur((c) => Math.min(Math.max(c + d, 0), max));
  };
  const prevLen = useRef(0);
  useEffect(() => {
    const old = prevLen.current;
    prevLen.current = max;
    setCur((c) => (old > 0 && max > 0 ? Math.min(max, Math.round((c / old) * max)) : 0));
  }, [max]);
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  });
  if (err) return <div className="min-h-screen flex items-center justify-center text-center p-6" data-testid="brochure-error"><div><Brand /><p className="mt-6 text-[#47606B]">{err}</p></div></div>;
  if (!b) return <div className="min-h-screen flex items-center justify-center text-sm text-[#7B929C]">Apertura della brochure…</div>;
  const shift = mobile ? 0 : cur === 0 ? -25 : cur === leaves.length ? 25 : 0;
  return (
    <div className={`min-h-screen bg-gradient-to-b from-[#0D2B3A] to-[#0B4D63] flex flex-col items-center justify-center px-3 overflow-hidden ${mobile ? "py-3" : "py-8"}`} data-testid="brochure-view">
      <style>{`.book{perspective:2200px}.leaf{transform-origin:left center;transform-style:preserve-3d;transition:transform 1.1s cubic-bezier(.645,.045,.355,1),opacity .4s ease 1s}.face{position:absolute;inset:0;backface-visibility:hidden;overflow:hidden;box-shadow:0 10px 30px rgba(0,0,0,.25)}.face.back{transform:rotateY(180deg)}.face.front:after{content:"";position:absolute;inset:0;pointer-events:none;background:linear-gradient(90deg,rgba(0,0,0,.12),transparent 8%)}.face.back:after{content:"";position:absolute;inset:0;pointer-events:none;background:linear-gradient(270deg,rgba(0,0,0,.12),transparent 8%)}.curl{position:absolute;right:0;bottom:0;width:0;height:0;background:linear-gradient(315deg,transparent 50%,rgba(255,255,255,.95) 50%,#dfeef1 60%,rgba(0,0,0,.18));transition:width .35s ease,height .35s ease;box-shadow:-3px -3px 8px rgba(0,0,0,.15)}.leaf:hover .curl{width:56px;height:56px}.brochure-scroll::-webkit-scrollbar{width:4px}.brochure-scroll::-webkit-scrollbar-thumb{background:#A8C6CF;border-radius:4px}.brochure-scroll{scrollbar-width:thin;scrollbar-color:#A8C6CF transparent}@keyframes kb{0%{transform:scale(1.04) translate(0,0)}100%{transform:scale(1.16) translate(-2%,-2%)}}.kb{animation:kb 16s ease-in-out infinite alternate}.leaf:not(.active) .kb{animation:none}@keyframes rise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}.rise{animation:rise .9s cubic-bezier(.2,.7,.2,1) both}@media (prefers-reduced-motion:reduce){.kb,.rise{animation:none}}`}</style>
      <div className={`text-white/70 text-[10px] tracking-[0.4em] uppercase text-center ${mobile ? "mb-2" : "mb-5"}`}>{b.lang === "en" ? `A proposal dedicated to ${b.nomi}` : <>Una proposta dedicata {/^[aeiouAEIOU]/.test(b.nomi || "") ? "ad" : "a"} {b.nomi}</>}</div>
      <div className="book relative transition-transform duration-700" style={mobile ? { width: "min(94vw, 480px)", height: "min(calc(100dvh - 150px), 860px)" } : { width: "min(1100px, 94vw, calc((100vh - 170px) * 1.414))", aspectRatio: "1.414", transform: `translateX(${shift}%)` }}
        onTouchStart={(e) => { touch.current = e.touches.length > 1 ? null : e.touches[0].clientX; }} onTouchMove={(e) => { if (e.touches.length > 1) touch.current = null; }}
        onTouchEnd={(e) => { if (touch.current == null || (window.visualViewport?.scale || 1) > 1.01) return; const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); }}>
        {leaves.map((leaf, i) => <Leaf key={i} leaf={leaf} i={i} mobile={mobile} near={Math.abs(i - cur) <= 2} active={mobile ? i === cur : i === cur || i === cur - 1} flipped={i < cur} z={i < cur ? i : leaves.length - i} onClick={() => go(i < cur ? -1 : 1)} />)}
      </div>
      <div className={`flex items-center text-white ${mobile ? "gap-2 mt-3" : "gap-4 mt-6"}`}>
        <button onClick={() => go(-1)} disabled={cur === 0} className="h-11 w-11 rounded-full border border-white/30 flex items-center justify-center disabled:opacity-30 hover:bg-white/10" data-testid="brochure-prev"><ChevronLeft size={18} /></button>
        <span className="text-xs tracking-widest" data-testid="brochure-page-indicator">{mobile ? `${cur + 1} / ${max + 1}` : cur === 0 ? "Copertina" : cur === leaves.length ? "Fine" : `${cur * 2}-${cur * 2 + 1} / ${faces.length}`}</span>
        <button onClick={() => go(1)} disabled={cur === max} className="h-11 w-11 rounded-full border border-white/30 flex items-center justify-center disabled:opacity-30 hover:bg-white/10" data-testid="brochure-next"><ChevronRight size={18} /></button>
        <a href={`${API}/public/brochure/${token}/pdf`} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs hover:bg-white/20" data-testid="brochure-pdf"><Download size={14} /> PDF</a>
        {b.music && <button onClick={toggleMusic} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs hover:bg-white/20" data-testid="brochure-music">{playing ? <Pause size={14} /> : <Music size={14} />} {playing ? "Pausa" : "Musica"}</button>}
        {b.music && <audio ref={audio} src={`${API}/public/music/${b.music}`} loop preload="auto" />}
      </div>
      {!mobile && <div className="text-white/40 text-[10px] mt-3">Toccate le pagine o scorrete per sfogliare</div>}
    </div>
  );
}
